// src/pages/ContactUs.jsx
import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import { motion } from "framer-motion";
import "../styles/ContactUs.css";

const ContactUs = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);
  const [officeInfo, setOfficeInfo] = useState(null);
  const [officeLoading, setOfficeLoading] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || !message) {
      setStatus({ type: "danger", msg: "All fields are required" });
      return;
    }

    const loggedUser = JSON.parse(localStorage.getItem("user"));
    const user_id = loggedUser?.user_id || "";

    setLoading(true);
    setStatus({ type: "", msg: "" });

    try {
      const res = await fetch(`${API_BASE_URL}feedback_insert.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user_id, // empty string if not logged in
          user_name: name,
          user_mail: email,
          user_message: message,
        }),
      });

      const data = await res.json();
      console.log("Feedback API Response:", data);

      if (data?.head?.code === 200) {
        setStatus({ type: "success", msg: data.head.msg });
        setName("");
        setEmail("");
        setMessage("");
      } else {
        setStatus({
          type: "danger",
          msg: data?.head?.msg || "Failed to send message",
        });
      }
    } catch (err) {
      console.error("Feedback error:", err);
      setStatus({ type: "danger", msg: "Server error. Try again later." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user?.user_id) return;

      try {
        const res = await fetch(`${API_BASE_URL}feedback_detail.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: user.user_id }),
        });

        const data = await res.json();
        console.log("Prefill User Data:", data);

        if (data?.head?.code === 200) {
          setName(data.body.user_name || "");
          setEmail(data.body.user_mail || "");
        }
      } catch (err) {
        console.error("Failed to fetch user info:", err);
      }
    };

    fetchUserDetails();
  }, [API_BASE_URL]);

  useEffect(() => {
    const fetchOfficeInfo = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}contactus_content_list.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });

        const data = await res.json();
        console.log("Contact Us Content:", data);

        if (data?.head?.code === 200 && data.body?.length) {
          setOfficeInfo(data.body[0]); // Only one Contact Us record
        } else {
          setOfficeInfo(null);
        }
      } catch (err) {
        console.error("Failed to load contact us content:", err);
        setOfficeInfo(null);
      } finally {
        setOfficeLoading(false);
      }
    };

    fetchOfficeInfo();
  }, [API_BASE_URL]);

  // Page loader
  const PageLoader = () => {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#ffffff",
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <div
        className="spinner-border"
        role="status"
        style={{ width: "3.5rem", height: "3.5rem", color: "#f64a1aff" }}
      />
      <p className="mt-3 fw-semibold">Loading Contact Page...</p>
    </div>
  );
};


  return (
    <>
    {officeLoading && <PageLoader />}
    <div style={{ paddingTop: "60px", paddingBottom: "60px" }}>
      {/* HERO SECTION */}
      <motion.section
        className="text-center d-flex align-items-center justify-content-center"
        style={{
          minHeight: "25vh",
          background: "#0b2747",
          color: "white",
          padding: "40px 20px",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="container">
          <motion.h1
            className="fw-bold contact-heading"
            initial={{ y: -40 }}
            animate={{ y: 0 }}
            transition={{ duration: 1 }}
          >
            Contact Us
          </motion.h1>

          <motion.p
            className="lead mt-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
          >
            We'd love to hear from you! Fill out the form or reach us directly.
          </motion.p>
        </div>
      </motion.section>

      {/* MAIN CONTENT */}
      <Container className="mt-5">
        <Row className="gy-5">
          {/* FORM SECTION */}
          <Col md={6}>
            <motion.div
              initial={{ x: -80, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="p-4 shadow rounded bg-white"
            >
              <h3 className="mb-3">Send us a message</h3>

              {status.msg && <Alert variant={status.type}>{status.msg}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3 text-start">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your name"
                  />
                </Form.Group>

                <Form.Group className="mb-3 text-start">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                  />
                </Form.Group>

                <Form.Group className="mb-3 text-start">
                  <Form.Label>Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Your message..."
                  />
                </Form.Group>

                <div className="text-start">
                  <Button type="submit" disabled={loading}>
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </div>
              </Form>
            </motion.div>
          </Col>

          {/* OFFICE INFO */}
          {/* OFFICE INFO */}
          <Col md={6}>
            <motion.div
              initial={{ x: 80, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="p-4 shadow rounded bg-white"
            >
              {/* <h3 className="mb-3">Our Office</h3> */}

              {officeLoading ? (
                <p>Loading office details...</p>
              ) : officeInfo ? (
                <>
                  {/* Office Content */}
                  {/* <div
                    className="text-start mb-4"
                    dangerouslySetInnerHTML={{ __html: officeInfo.content }}
                  /> */}
                  <div>
                    <h5>Our Office</h5>
                    Name : {officeInfo.content_title}<br />
                    Address : {officeInfo.address}<br />
                    Phone : {officeInfo.mobile}<br />
                    Email : {officeInfo.email}
                    <p></p>

                  </div>

                  {/* Google Map */}
                  {officeInfo.map_url && (
                    <iframe
                      title="Office Location"
                      src={officeInfo.map_url}
                      width="100%"
                      height="300"
                      style={{ border: 0, borderRadius: "12px" }}
                      loading="lazy"
                    ></iframe>
                  )}
                </>
              ) : (
                <p>No office information available.</p>
              )}
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
    </>
  );
};

export default ContactUs;
